import React, { useState } from "react";
import {
  ArrowDown, ArrowUp, Code2, ListPlus, MessageSquarePlus, Trash2, X,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, Callout, FieldError, FormField, IconButton, Input,
  SegmentedControl, Textarea, cn,
} from "../../ui";
import RandomRulesEditor from "./RandomRulesEditor";
import SectionMark from "./SectionMark";
import { DIFFICULTY_LABELS, LIMITS, QUESTION_TYPE_LABELS, letterOf } from "./builderRules";

const PREVIEW_ROWS = 8;

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** One chosen question: its text, what kind it is, and a way to take it off. */
function QuestionRow({ q, index, onRemove, disabled }) {
  const meta = [
    QUESTION_TYPE_LABELS[q.questionType],
    DIFFICULTY_LABELS[q.difficulty],
    plural(Number(q.overrideMarks ?? q.marks ?? 1), "mark"),
  ].filter(Boolean).join(" · ");
  return (
    <li className="tw-flex tw-items-start tw-gap-3 tw-py-2 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
      <span className="tw-mt-0.5 tw-w-6 tw-shrink-0 tw-text-right tw-text-xs tw-tabular-nums tw-text-content-subtle">{index + 1}.</span>
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
        <span className="tw-line-clamp-2 tw-text-[0.8125rem] tw-text-content">{q.questionText}</span>
        <span className="tw-text-xs tw-text-content-subtle">{meta}</span>
      </span>
      <IconButton icon={X} label={`Remove question ${index + 1}`} size="sm" onClick={onRemove} disabled={disabled} />
    </li>
  );
}

/** One coding problem on the paper, with the marks it is worth. */
function CodingRow({ c, index, sectionKey, onMarks, onRemove, disabled }) {
  const tests = [c.sampleCount ? `${c.sampleCount} sample` : "", c.hiddenCount ? `${c.hiddenCount} hidden` : ""]
    .filter(Boolean).join(" + ");
  const meta = c.missing
    ? "Deleted from the coding bank — remove it from this paper."
    : [DIFFICULTY_LABELS[c.difficulty], (c.languages || []).slice(0, 4).join(", "), tests && `${tests} tests`]
      .filter(Boolean).join(" · ");
  return (
    <li className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-py-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line first:tw-border-t-0">
      <Code2 size={16} aria-hidden="true" className="tw-shrink-0 tw-text-content-muted" />
      <span className="tw-flex tw-min-w-[12rem] tw-flex-1 tw-flex-col tw-gap-0.5">
        <span className="tw-text-[0.8125rem] tw-font-medium tw-text-content">{c.title}</span>
        <span className={cn("tw-text-xs", c.missing ? "tw-text-danger" : "tw-text-content-subtle")}>{meta}</span>
      </span>
      <span className="tw-w-28">
        <Input
          type="number"
          inputMode="decimal"
          size="sm"
          min={1}
          max={LIMITS.codingMarks}
          aria-label={`Marks for ${c.title}`}
          id={`${sectionKey}-coding-marks-${index}`}
          value={c.marks}
          onChange={(e) => onMarks(e.target.value === "" ? "" : Number(e.target.value))}
          trailing={<span className="tw-text-xs">marks</span>}
          trailingPad="text"
          disabled={disabled}
        />
      </span>
      <IconButton icon={X} label={`Remove ${c.title}`} size="sm" onClick={onRemove} disabled={disabled} />
    </li>
  );
}

/**
 * SectionCard — one section of the paper: its name, a note for students, the
 * questions (chosen one by one, or a random draw) and its coding problems.
 *
 * Both question sources are kept while the author switches between them, so
 * trying "random" and going back to "pick" loses nothing; only the one showing
 * is saved.
 */
export default function SectionCard({
  section, index, count, sectioned, categories, coding, problems = [],
  onChange, onMove, onRemove, onPickQuestions, onPickCoding, locked,
}) {
  const [showAll, setShowAll] = useState(false);
  const [noteOpen, setNoteOpen] = useState(!!section.description);
  const name = section.title?.trim() || (sectioned ? `Section ${letterOf(index)}` : "Questions");
  const questions = section.questions || [];
  const shown = showAll ? questions : questions.slice(0, PREVIEW_ROWS);
  const [moreOpen, setMoreOpen] = useState(false);
  const hasCoding = (section.coding || []).length > 0;
  const hasQuestions = questions.length > 0 || (section.rules || []).length > 0 || section.source === "random";
  // A section shows what it is FOR first — a coding section its problems, any
  // other its questions — and the other kind only once it is used or asked for.
  // Where Coding Practice is off nothing here mentions coding, unless the paper
  // already holds problems that have to come off before it can be saved.
  const codingSection = section.intent === "coding" && (coding.shown || hasCoding);
  const codingFirst = codingSection && !hasQuestions;
  const showQuestions = !codingFirst || moreOpen || !coding.allowed;
  const showCoding = hasCoding || (coding.shown && (section.intent === "coding" || (moreOpen && coding.allowed)));
  const offerMore = !locked && (codingFirst ? !showQuestions : coding.allowed && !showCoding);
  const empty = questions.length === 0 && (section.rules || []).length === 0 && !hasCoding;

  const questionBlock = showQuestions && (
    <div className="tw-flex tw-flex-col tw-gap-3" key="questions">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
          {codingSection ? "Other questions" : "Questions"}
        </h4>
        <SegmentedControl
          label={`How questions are chosen in ${name}`}
          value={section.source}
          onChange={(v) => onChange({ source: v })}
          options={[
            { value: "pick", label: "Pick from bank", count: questions.length || undefined },
            { value: "random", label: "Random draw" },
          ]}
        />
      </div>

      {section.source === "pick" ? (
        <>
          {questions.length > 0 && (
            <ol className="tw-m-0 tw-list-none tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-1">
              {shown.map((q, i) => (
                <QuestionRow
                  key={q.question}
                  q={q}
                  index={i}
                  disabled={locked}
                  onRemove={() => onChange({ questions: questions.filter((x) => x.question !== q.question) })}
                />
              ))}
            </ol>
          )}
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            <AppButton
              type="button"
              size="sm"
              variant={questions.length || codingSection ? "outline" : "default"}
              onClick={onPickQuestions}
              disabled={locked}
            >
              <ListPlus size={16} aria-hidden="true" />
              {questions.length ? "Add or remove questions" : "Add questions from the bank"}
            </AppButton>
            {questions.length > PREVIEW_ROWS && (
              <AppButton type="button" size="sm" variant="ghost" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Show fewer" : `Show all ${questions.length}`}
              </AppButton>
            )}
            {questions.length > 0 && (
              <AppButton type="button" size="sm" variant="ghost-destructive" onClick={() => onChange({ questions: [] })} disabled={locked}>
                Remove all
              </AppButton>
            )}
          </div>
        </>
      ) : (
        <RandomRulesEditor
          rules={section.rules || []}
          onChange={(rules) => onChange({ rules })}
          categories={categories}
          disabled={locked}
          idPrefix={section.key}
        />
      )}
    </div>
  );

  const codingBlock = showCoding && (
    <div className="tw-flex tw-flex-col tw-gap-3" key="coding">
      <div className="tw-flex tw-flex-col tw-gap-0.5">
        <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Coding problems</h4>
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
          Students write and run a program; it is marked on the test cases it passes.
        </p>
      </div>
      {!coding.allowed && (
        <Callout tone="warning">
          {coding.reason || "Coding problems are not available."}{" "}
          {coding.shown
            ? "You can still ask code-reading questions (the \"Code\" question type) from your question bank."
            : "Take these coding problems off to save this assessment."}
        </Callout>
      )}
      {(section.coding || []).length > 0 && (
        <ul className="tw-m-0 tw-list-none tw-rounded-md tw-border tw-border-solid tw-border-line tw-px-3 tw-py-0">
          {section.coding.map((c, i) => (
            <CodingRow
              key={c.question}
              c={c}
              index={i}
              sectionKey={section.key}
              disabled={locked}
              onMarks={(marks) => onChange({ coding: section.coding.map((x) => (x.question === c.question ? { ...x, marks } : x)) })}
              onRemove={() => onChange({ coding: section.coding.filter((x) => x.question !== c.question) })}
            />
          ))}
        </ul>
      )}
      {coding.allowed && (
        <div>
          <AppButton
            type="button"
            size="sm"
            variant={hasCoding || section.intent !== "coding" ? "outline" : "default"}
            onClick={onPickCoding}
            disabled={locked}
          >
            <Code2 size={16} aria-hidden="true" />
            {(section.coding || []).length ? "Add or remove coding problems" : "Add coding problems"}
          </AppButton>
        </div>
      )}
    </div>
  );

  const qCount = section.source === "random"
    ? (section.rules || []).reduce((sum, r) => sum + (Number(r.count) || 0), 0)
    : questions.length;
  const summary = [
    qCount ? plural(qCount, section.source === "random" ? "random question" : "question") : "",
    (section.coding || []).length ? plural(section.coding.length, "coding problem") : "",
  ].filter(Boolean).join(" · ") || "Nothing added yet";

  return (
    <AppCard as="section" aria-label={name} overflowVisible>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-flex tw-items-start tw-gap-3">
          {sectioned && <SectionMark letter={letterOf(index)} className="tw-mt-6" />}
          <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
            {sectioned ? (
              <FormField label={`Section ${letterOf(index)} name`} labelTone="muted">
                <Input
                  value={section.title}
                  onChange={(e) => onChange({ title: e.target.value })}
                  placeholder={`e.g. ${["Aptitude & English", coding.shown ? "Coding" : "Reasoning", "Technical"][index % 3]}`}
                  maxLength={LIMITS.sectionTitle}
                  disabled={locked}
                />
              </FormField>
            ) : (
              <h3 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Questions</h3>
            )}
            <span className="tw-text-xs tw-text-content-subtle">{summary}</span>
          </div>
          {sectioned && count > 1 && (
            <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5 tw-pt-6">
              <IconButton icon={ArrowUp} label={`Move ${name} up`} size="sm" onClick={() => onMove(-1)} disabled={locked || index === 0} />
              <IconButton icon={ArrowDown} label={`Move ${name} down`} size="sm" onClick={() => onMove(1)} disabled={locked || index === count - 1} />
              <IconButton icon={Trash2} label={`Remove ${name}`} size="sm" variant="ghost-destructive" onClick={onRemove} disabled={locked} />
            </div>
          )}
        </div>

        {empty && section.hint && (
          <p className="tw-m-0 tw-rounded-md tw-bg-muted/60 tw-px-3 tw-py-2 tw-text-[0.8125rem] tw-text-content-muted">{section.hint}</p>
        )}

        {sectioned && (
          noteOpen ? (
            <FormField label="Note for students" hint="Shown at the start of this section. Optional.">
              <Textarea
                rows={2}
                value={section.description}
                onChange={(e) => onChange({ description: e.target.value })}
                maxLength={LIMITS.sectionNote}
                placeholder="e.g. Use the rough sheet for calculations. Calculators are not allowed."
                disabled={locked}
              />
            </FormField>
          ) : (
            <div>
              <AppButton type="button" size="xs" variant="ghost" onClick={() => setNoteOpen(true)} disabled={locked}>
                <MessageSquarePlus size={14} aria-hidden="true" /> Add a note for students
              </AppButton>
            </div>
          )
        )}

        {codingSection ? [codingBlock, questionBlock] : [questionBlock, codingBlock]}

        {offerMore && (
          <div>
            <AppButton type="button" size="xs" variant="ghost" onClick={() => (codingFirst ? setMoreOpen(true) : onPickCoding())}>
              {codingFirst
                ? <><ListPlus size={14} aria-hidden="true" /> Also add questions from the bank</>
                : <><Code2 size={14} aria-hidden="true" /> Add a coding problem to this section</>}
            </AppButton>
          </div>
        )}

        {problems.length > 0 && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            {problems.map((p) => <FieldError key={p}>{p}</FieldError>)}
          </div>
        )}
      </AppCardContent>
    </AppCard>
  );
}
